import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Select, Button, Typography, Divider, Modal, Input, Steps, Tooltip, Switch, Tag, Space } from 'antd';
import { Zap, FileText, Tags, Target, CheckCircle, Clock, AlertCircle, Bot, Send, Youtube, Globe, Link, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../components/Axios';

const { Option, OptGroup } = Select;
const { Text } = Typography;
const { TextArea } = Input;

// ── Variable chip lists ───────────────────────────────────────────────────────
// {brand_voice} added to both Website and YouTube lists

const WEBSITE_VARIABLES = [
  { label: '{question}',         color: 'blue',    description: 'The Quora question text' },
  { label: '{keyword}',          color: 'green',   description: 'Your selected keyword' },
  { label: '{cta}',              color: 'orange',  description: 'Your selected call-to-action' },
  { label: '{basic_info}',       color: 'purple',  description: 'Basic info from your angle' },
  { label: '{existing_answers}', color: 'cyan',    description: 'Existing Quora answers on this question' },
  { label: '{brand_voice}',      color: 'magenta', description: 'Brand voice & writing style from your angle — silently removed if empty' },
];

const YOUTUBE_VARIABLES = [
  { label: '{question}',         color: 'blue',    description: 'The Quora question text' },
  { label: '{keyword}',          color: 'green',   description: 'Your selected keyword (optional in YouTube mode)' },
  { label: '{cta}',              color: 'orange',  description: 'Your selected call-to-action (optional in YouTube mode)' },
  { label: '{basic_info}',       color: 'purple',  description: 'Basic info from your angle' },
  { label: '{existing_answers}', color: 'cyan',    description: 'Existing Quora answers on this question' },
  { label: '{transcript}',       color: 'red',     description: 'YouTube video transcript — auto-injected at generation time' },
  { label: '{youtube_url}',      color: 'volcano', description: 'Your YouTube video URL — injected into the answer so Quora auto-embeds it' },
  { label: '{brand_voice}',      color: 'magenta', description: 'Brand voice & writing style from your angle — silently removed if empty' },
];

const VariableChips = ({ textareaRef, value, onChange, isYoutubeMode }) => {
  const variables = isYoutubeMode ? YOUTUBE_VARIABLES : WEBSITE_VARIABLES;

  const insertVariable = (variable) => {
    const el = textareaRef?.current?.resizableTextArea?.textArea || textareaRef?.current;
    if (!el) { onChange((value || '') + variable); return; }
    const start = el.selectionStart ?? (value || '').length;
    const end   = el.selectionEnd   ?? (value || '').length;
    const newVal = (value || '').substring(0, start) + variable + (value || '').substring(end);
    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + variable.length, start + variable.length); }, 0);
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
        💡 Click a chip to insert it at your cursor position in the prompt:
      </Text>
      <Space wrap size={[6, 6]}>
        {variables.map(v => (
          <Tooltip key={v.label} title={v.description}>
            <Tag color={v.color} style={{ cursor: 'pointer', fontSize: '12px', padding: '2px 8px', userSelect: 'none' }} onClick={() => insertVariable(v.label)}>
              {v.label}
            </Tag>
          </Tooltip>
        ))}
      </Space>
    </div>
  );
};

const AI_MODELS = {
  openai: {
    label: 'OpenAI (ChatGPT)',
    icon: '🤖',
    models: [
      { label: 'GPT-5.4 Pro',        value: 'openai::gpt-5.4-pro',          apiModel: 'gpt-5.4-pro',   apiType: 'responses' },
      { label: 'GPT-5.4 Standard',   value: 'openai::gpt-5.4',              apiModel: 'gpt-5.4',       apiType: 'responses' },
      { label: 'GPT-5.4 Thinking',   value: 'openai::gpt-5.4-thinking',     apiModel: 'gpt-5.4',       apiType: 'responses' },
      { label: 'GPT-5.4 mini',       value: 'openai::gpt-5.4-mini',         apiModel: 'gpt-5.4-mini',  apiType: 'responses' },
      { label: 'GPT-5.4 nano',       value: 'openai::gpt-5.4-nano',         apiModel: 'gpt-5.4-nano',  apiType: 'responses' },
      { label: 'GPT-5.3 Instant',    value: 'openai::gpt-5.3',              apiModel: 'gpt-5.3',       apiType: 'responses' },
      { label: 'GPT-5.1 (Stable)',   value: 'openai::gpt-5.1',              apiModel: 'gpt-5.1',       apiType: 'responses' },
      { label: 'GPT-4.1 (Omni-v2)', value: 'openai::gpt-4.1',              apiModel: 'gpt-4.1',       apiType: 'chat' },
    ]
  },
  gemini: {
    label: 'Google Gemini',
    icon: '✨',
    models: [
      { label: 'Gemini 3.1 Pro',        value: 'gemini::gemini-3.1-pro-preview',        apiModel: 'gemini-3.1-pro-preview' },
      { label: 'Gemini 3.1 Flash',      value: 'gemini::gemini-3-flash-preview',         apiModel: 'gemini-3-flash-preview' },
      { label: 'Gemini 3.1 Flash-Lite', value: 'gemini::gemini-3.1-flash-lite-preview',  apiModel: 'gemini-3.1-flash-lite-preview' },
      { label: 'Gemini 2.5 Pro',        value: 'gemini::gemini-2.5-pro',                apiModel: 'gemini-2.5-pro' },
      { label: 'Gemini 2.5 Flash',      value: 'gemini::gemini-2.5-flash',              apiModel: 'gemini-2.5-flash' },
      { label: 'Gemini 2.5 Flash-Lite', value: 'gemini::gemini-2.5-flash-lite',         apiModel: 'gemini-2.5-flash-lite' },
    ]
  },
  claude: {
    label: 'Anthropic Claude',
    icon: '🧠',
    models: [
      { label: 'Claude Opus 4.6',   value: 'claude::claude-opus-4-6',          apiModel: 'claude-opus-4-6' },
      { label: 'Claude Sonnet 4.6', value: 'claude::claude-sonnet-4-6',        apiModel: 'claude-sonnet-4-6' },
      { label: 'Claude Haiku 4.5',  value: 'claude::claude-haiku-4-5',         apiModel: 'claude-haiku-4-5-20251001' },
      { label: 'Claude Opus 4.5',   value: 'claude::claude-opus-4-5',          apiModel: 'claude-opus-4-5' },
      { label: 'Claude Sonnet 4.5', value: 'claude::claude-sonnet-4-5',        apiModel: 'claude-sonnet-4-5' },
      { label: 'Claude Sonnet 4',   value: 'claude::claude-sonnet-4-20250514', apiModel: 'claude-sonnet-4-20250514' },
    ]
  }
};

const resolveModel = (value) => {
  if (!value) return null;
  const [provider] = value.split('::');
  const group = AI_MODELS[provider];
  if (!group) return null;
  const found = group.models.find(m => m.value === value);
  return found ? { provider, apiModel: found.apiModel, apiType: found.apiType || 'chat', label: found.label, providerLabel: group.label } : null;
};

const AiAnswerGenerator = ({
  userId,
  questionData,
  questions,
  selectedQuestionFromTable,
  onRefreshData,
  editedContentMap = {},
}) => {
  const [angles, setAngles]                   = useState([]);
  const [selectedAngle, setSelectedAngle]     = useState(null);
  const [selectedAngleDetails, setSelectedAngleDetails] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [selectedCta, setSelectedCta]         = useState(null);
  const [selectedModelValue, setSelectedModelValue] = useState(null);
  const [selectedQuestionData, setSelectedQuestionData] = useState(null);
  const [promptModalVisible, setPromptModalVisible] = useState(false);
  const [editablePrompt, setEditablePrompt]   = useState('');
  const [loadingAngles, setLoadingAngles]     = useState(false);

  const [isYoutubeMode, setIsYoutubeMode] = useState(false);
  const [youtubeUrl, setYoutubeUrl]       = useState('');

  const [generatingSelected, setGeneratingSelected] = useState(false);
  const [generatedAnswer, setGeneratedAnswer]       = useState(null);
  const [bulkGenerating, setBulkGenerating]         = useState(false);
  const [bulkPublishing, setBulkPublishing]         = useState(false);
  const [publishingSelected, setPublishingSelected] = useState(false);

  const promptTextareaRef = useRef(null);

  // ── Prompt Library state ──────────────────────────────────────────────────
  const [promptLibrary, setPromptLibrary]               = useState([]);
  const [promptLibraryModal, setPromptLibraryModal]     = useState(false);
  const [selectedLibraryPrompt, setSelectedLibraryPrompt] = useState(null);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const extractQuestionFromUrl = (questionLink) => {
    try {
      if (!questionLink || typeof questionLink !== 'string') return '';
      const url = new URL(questionLink);
      let t = url.pathname.replace(/^\//, '').split('-').join(' ').replace(/[^a-zA-Z0-9\s?.,!]/g, ' ').replace(/\s+/g, ' ').trim();
      if (t) { t = t.charAt(0).toUpperCase() + t.slice(1); if (!t.endsWith('?') && !t.endsWith('.') && !t.endsWith('!')) t += '?'; }
      return t;
    } catch { return ''; }
  };

  const formatAnswersForPrompt = (answers) => {
    if (!answers?.length) return 'No existing answers found on this Quora question.';
    return answers.map((a, i) => `Answer ${i + 1}:\nViews: ${a.views || 'Unknown'}\nContent: ${a.content || 'No content'}\n\n---`).join('\n');
  };

  // ── Build Prompt ──────────────────────────────────────────────────────────
  // {brand_voice} replacement added — empty string = silent removal from prompt
  const buildPromptForQuestion = (q, transcriptText = '') => {
    const base = editablePrompt.trim();
    if (!base) return '';
    const replacements = {
      '{question}':         extractQuestionFromUrl(q.questionLink),
      '{keyword}':          selectedKeyword || '',
      '{cta}':              selectedCta     || '',
      '{basic_info}':       selectedAngleDetails?.basicInfo   || '',
      '{brand_voice}':      selectedAngleDetails?.brandVoice  || '',  // NEW
      '{existing_answers}': formatAnswersForPrompt(q.answers  || []),
      '{transcript}':       isYoutubeMode ? (transcriptText  || '') : '',
      '{youtube_url}':      isYoutubeMode ? (youtubeUrl.trim() || '') : '',
    };
    let prompt = base;
    Object.entries(replacements).forEach(([variable, value]) => { prompt = prompt.split(variable).join(value); });
    const usesVariables = Object.keys(replacements).some(v => base.includes(v));
    if (!usesVariables) {
      prompt = `Quora Question: ${replacements['{question}']}\n\nExisting Answers on Quora:\n${replacements['{existing_answers}']}`;
      if (selectedKeyword?.trim())                 prompt += `\n\nKeyword to Focus On: ${selectedKeyword}`;
      if (selectedCta?.trim())                     prompt += `\n\nCall-to-Action to Include: ${selectedCta}`;
      if (isYoutubeMode && transcriptText?.trim()) prompt += `\n\nYouTube Video Transcript:\n${transcriptText}`;
      if (isYoutubeMode && youtubeUrl?.trim())     prompt += `\n\nYouTube Video URL (embed this naturally in the answer): ${youtubeUrl}`;
      prompt += `\n\n${base}`;
    }
    console.log("prompt", prompt);
    return prompt;
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateSetup = () => {
    if (!selectedAngle)      { toast.warning('⚠️ Please select an Angle first.');  return false; }
    if (!selectedModelValue) { toast.warning('⚠️ Please select an AI Model.');      return false; }
    if (!isYoutubeMode) {
      if (!selectedKeyword)  { toast.warning('⚠️ Please select a Keyword.');        return false; }
      if (!selectedCta)      { toast.warning('⚠️ Please select a CTA.');            return false; }
    }
    if (isYoutubeMode && !youtubeUrl.trim()) { toast.warning('⚠️ YouTube mode is ON — please paste a YouTube URL.'); return false; }
    if (!selectedLibraryPrompt)              { toast.warning('⚠️ Please select a Prompt.'); return false; }
    return true;
  };

  const validateSelectedQuestion = () => {
    if (!selectedQuestionData) { toast.warning('⚠️ No question selected. Please select a question from the table below.'); return false; }
    if (!selectedQuestionData.answers?.length) { toast.warning('⚠️ Selected question has no scraped answers yet.'); return false; }
    return true;
  };

  // ── Fetch angles ──────────────────────────────────────────────────────────
  const fetchUserAngles = async () => {
    if (!userId) return;
    setLoadingAngles(true);
    try {
      const res = await axios.get(`/api/user-angles/${userId}`);
      if (res.data?.success) setAngles(res.data.data);
    } catch { toast.error('Failed to fetch angles'); }
    finally { setLoadingAngles(false); }
  };

  // ── Fetch Prompt Library ──────────────────────────────────────────────────
  const loadPromptLibrary = async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`/api/prompt-library/${userId}`);
      if (res.data.success) setPromptLibrary(res.data.data);
    } catch { console.error('Failed to load prompt library'); }
  };

  const handleAngleSelect = (angleId) => {
    setSelectedAngle(angleId);
    setSelectedAngleDetails(angles.find(a => a._id === angleId));
    setSelectedKeyword(null);
    setSelectedCta(null);
    setEditablePrompt('');
    setSelectedLibraryPrompt(null);
  };

  const handlePromptModalOpen = () => {
    setPromptModalVisible(true);
  };

  // ── Select prompt from library ────────────────────────────────────────────
  const handleSelectFromLibrary = (prompt) => {
    setSelectedLibraryPrompt(prompt);
    setEditablePrompt(prompt.content);
    setPromptLibraryModal(false);
    toast.success(`✅ "${prompt.name}" selected!`);
  };

  const handleClearLibraryPrompt = () => {
    setSelectedLibraryPrompt(null);
    setEditablePrompt('');
    toast.info('Cleared — using default prompt');
  };

  // ── Fetch transcript ──────────────────────────────────────────────────────
  const fetchTranscript = async () => {
    if (!youtubeUrl.trim()) return '';
    try {
      const res = await axios.post('/api/youtube-transcript', { youtubeUrl });
      if (res.data?.success) return res.data.transcript || '';
      toast.error(res.data?.error || 'Failed to fetch transcript');
      return '';
    } catch (err) {
      toast.error(err.response?.data?.error || '❌ Could not fetch YouTube transcript');
      return '';
    }
  };

  // ── Generate Selected ─────────────────────────────────────────────────────
  const handleGenerateSelected = async () => {
    if (!validateSetup() || !validateSelectedQuestion()) return;
    const resolved = resolveModel(selectedModelValue);
    if (!resolved) { toast.error('Invalid model selected'); return; }
    setGeneratingSelected(true);
    const toastId = toast.loading(isYoutubeMode ? '🎬 Fetching YouTube transcript...' : `Generating answer with ${resolved.label}...`);
    try {
      let transcriptText = '';
      if (isYoutubeMode) {
        transcriptText = await fetchTranscript();
        if (!transcriptText) { toast.update(toastId, { render: '❌ Transcript fetch failed.', type: 'error', isLoading: false, autoClose: 5000 }); setGeneratingSelected(false); return; }
        toast.update(toastId, { render: `Generating answer with ${resolved.label}...`, type: 'default', isLoading: true });
      }
      const res = await axios.post('/api/generate-ai-answer', {
        userId, questionId: questionData._id, angleId: selectedAngle,
        selectedQuestionLink: selectedQuestionData.questionLink,
        aiProvider: resolved.provider, model: resolved.apiModel,
        prompt: buildPromptForQuestion(selectedQuestionData, transcriptText),
        selectedKeyword, selectedCta,
      });
      if (res.data?.success) {
        toast.update(toastId, { render: '✅ Answer generated!', type: 'success', isLoading: false, autoClose: 3000 });
        setGeneratedAnswer({ content: res.data.data?.generatedContent, questionLink: selectedQuestionData.questionLink, questionRowId: selectedQuestionData._id });
        await onRefreshData();
      }
    } catch (err) {
      toast.update(toastId, { render: err.response?.data?.error || '❌ Failed to generate answer', type: 'error', isLoading: false, autoClose: 5000 });
    } finally { setGeneratingSelected(false); }
  };

  // ── Publish Selected ──────────────────────────────────────────────────────
  const handlePublishSelected = () => {
    if (!selectedQuestionData) { toast.warning('⚠️ No question selected.'); return; }
    const content = editedContentMap[selectedQuestionData._id] !== undefined
      ? editedContentMap[selectedQuestionData._id]
      : selectedQuestionData.generatedAnswer?.generatedContent || generatedAnswer?.content;
    if (!content) { toast.warning('⚠️ No AI answer found. Generate it first!'); return; }
    setPublishingSelected(true);
    console.log("answer", content);
    window.postMessage({ action: 'publishToQuora', answer: content, questionLink: selectedQuestionData.questionLink }, '*');
    toast.success('📤 Selected answer sent to Quora!');
    setTimeout(() => setPublishingSelected(false), 1500);
  };

  // ── Generate All ──────────────────────────────────────────────────────────
  const handleGenerateAllAnswers = async () => {
    if (!validateSetup()) return;
    const resolved = resolveModel(selectedModelValue);
    if (!resolved) { toast.error('Invalid model selected'); return; }
    const toGenerate = (questions || []).filter(q => !q.generatedAnswer?.generatedContent);
    if (!toGenerate.length) { toast.info('ℹ️ All questions already have generated answers!'); return; }
    setBulkGenerating(true);
    let transcriptText = '';
    if (isYoutubeMode) {
      const tId = toast.loading('🎬 Fetching YouTube transcript...');
      transcriptText = await fetchTranscript();
      if (!transcriptText) { toast.update(tId, { render: '❌ Transcript fetch failed.', type: 'error', isLoading: false, autoClose: 5000 }); setBulkGenerating(false); return; }
      toast.update(tId, { render: '✅ Transcript fetched! Starting generation...', type: 'success', isLoading: false, autoClose: 2000 });
    }
    const toastId = toast.loading(`Starting: 0 of ${toGenerate.length} answers generating...`);
    for (let i = 0; i < toGenerate.length; i++) {
      const q = toGenerate[i];
      toast.update(toastId, { render: `Generating ${i + 1} of ${toGenerate.length}...`, type: 'default', isLoading: true });
      try {
        const res = await axios.post('/api/generate-ai-answer', {
          userId, questionId: questionData._id, angleId: selectedAngle,
          selectedQuestionLink: q.questionLink,
          aiProvider: resolved.provider, model: resolved.apiModel,
          prompt: buildPromptForQuestion(q, transcriptText),
          selectedKeyword, selectedCta,
        });
        if (!res.data?.success) throw new Error(res.data?.error || 'Generation failed');
        if (i < toGenerate.length - 1) await new Promise(r => setTimeout(r, 1500));
      } catch (err) {
        toast.update(toastId, { render: `❌ Failed on Q${i + 1}: ${err.response?.data?.error || err.message}`, type: 'error', isLoading: false, autoClose: 7000 });
        setBulkGenerating(false);
        await onRefreshData();
        return;
      }
    }
    await onRefreshData();
    toast.update(toastId, { render: `✅ All ${toGenerate.length} answers generated!`, type: 'success', isLoading: false, autoClose: 5000 });
    setBulkGenerating(false);
  };

  // ── Publish All ───────────────────────────────────────────────────────────
  const handlePublishAllAnswers = async () => {
    const toPublish = (questions || []).filter(q => q.generatedAnswer?.generatedContent && q.questionLink);
    if (!toPublish.length) { toast.warning('⚠️ No generated answers to publish.'); return; }
    setBulkPublishing(true);
    const toastId = toast.loading(`Starting: 0 of ${toPublish.length} answers publishing...`);
    for (let i = 0; i < toPublish.length; i++) {
      const q = toPublish[i];
      toast.update(toastId, { render: `Publishing ${i + 1} of ${toPublish.length}...`, type: 'default', isLoading: true });
      try {
        const content = editedContentMap[q._id] !== undefined ? editedContentMap[q._id] : q.generatedAnswer.generatedContent;
        window.postMessage({ action: 'publishToQuora', answer: content, questionLink: q.questionLink }, '*');
        if (i < toPublish.length - 1) await new Promise(r => setTimeout(r, 1500));
      } catch (err) {
        toast.update(toastId, { render: `❌ Failed on publish ${i + 1}: ${err.message}`, type: 'error', isLoading: false, autoClose: 7000 });
        setBulkPublishing(false);
        return;
      }
    }
    toast.update(toastId, { render: `✅ All ${toPublish.length} answers sent to Quora!`, type: 'success', isLoading: false, autoClose: 5000 });
    setBulkPublishing(false);
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const getCurrentStep = () => {
    if (!selectedAngle)               return 0;
    if (!selectedModelValue)          return 1;
    if (isYoutubeMode) {
      if (!youtubeUrl.trim())         return 2;
      if (!selectedLibraryPrompt)     return 3;
      return 4;
    }
    if (!selectedKeyword)             return 2;
    if (!selectedCta)                 return 3;
    if (!selectedLibraryPrompt)       return 4;
    return 5;
  };
  const isSetupReady      = isYoutubeMode
    ? !!(selectedAngle && selectedModelValue && youtubeUrl.trim() && selectedLibraryPrompt)
    : !!(selectedAngle && selectedModelValue && selectedKeyword && selectedCta && selectedLibraryPrompt);
  const isReadyToGenerate = isSetupReady && !!selectedQuestionData;
  const resolvedModel     = resolveModel(selectedModelValue);
  const anyBusy           = generatingSelected || bulkGenerating || bulkPublishing || publishingSelected;

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { if (userId) fetchUserAngles(); }, [userId]);
  useEffect(() => { if (userId) loadPromptLibrary(); }, [userId]);

  useEffect(() => {
    if (selectedQuestionFromTable) {
      setSelectedQuestionData(selectedQuestionFromTable);
      const content = selectedQuestionFromTable.generatedAnswer?.generatedContent || '';
      setGeneratedAnswer(content ? { content, questionLink: selectedQuestionFromTable.questionLink, questionRowId: selectedQuestionFromTable._id } : null);
    }
  }, [selectedQuestionFromTable]);

  useEffect(() => {
    setSelectedLibraryPrompt(null);
    setEditablePrompt('');
  }, [isYoutubeMode]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ marginTop: '10px' }}>
        <Card
          style={{ marginBottom: '24px' }}
          loading={loadingAngles}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={18} />
                <span>AI Answer Generation</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '20px', background: !isYoutubeMode ? '#eff6ff' : '#f9fafb', border: `1px solid ${!isYoutubeMode ? '#bfdbfe' : '#e5e7eb'}` }}>
                  <Globe size={14} color={!isYoutubeMode ? '#2563eb' : '#9ca3af'} />
                  <Text style={{ fontSize: '13px', color: !isYoutubeMode ? '#2563eb' : '#9ca3af', fontWeight: !isYoutubeMode ? 600 : 400 }}>Website</Text>
                </div>
                <Switch checked={isYoutubeMode} onChange={(checked) => { setIsYoutubeMode(checked); setYoutubeUrl(''); setSelectedKeyword(null); setSelectedCta(null); }} style={{ backgroundColor: isYoutubeMode ? '#ef4444' : undefined }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '20px', background: isYoutubeMode ? '#fef2f2' : '#f9fafb', border: `1px solid ${isYoutubeMode ? '#fecaca' : '#e5e7eb'}` }}>
                  <Youtube size={14} color={isYoutubeMode ? '#ef4444' : '#9ca3af'} />
                  <Text style={{ fontSize: '13px', color: isYoutubeMode ? '#ef4444' : '#9ca3af', fontWeight: isYoutubeMode ? 600 : 400 }}>YouTube</Text>
                </div>
              </div>
            </div>
          }
        >
          {/* YouTube URL input */}
          {isYoutubeMode && (
            <div style={{ marginBottom: '16px', padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Youtube size={15} color="#ef4444" />
                <Text strong style={{ color: '#dc2626', fontSize: '13px' }}>YouTube Mode — Paste a video URL to use its transcript in the prompt</Text>
              </div>
              <Input prefix={<Link size={14} color="#9ca3af" />} placeholder="https://www.youtube.com/watch?v=..." value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} size="large" allowClear style={{ borderColor: youtubeUrl.trim() ? '#ef4444' : '#d1d5db' }} />
              <Text type="secondary" style={{ fontSize: '11px', marginTop: '6px', display: 'block' }}>
                Transcript is fetched automatically when you click Generate. Use <code style={{ background: '#fee2e2', padding: '0 4px', borderRadius: '3px' }}>{'{transcript}'}</code> in your prompt to inject it.
              </Text>
            </div>
          )}

          {/* Steps */}
          <div style={{ marginBottom: '24px' }}>
            <Steps current={getCurrentStep()} size="small" items={isYoutubeMode ? [
              { title: 'Angle',    description: selectedAngle         ? 'Selected'          : 'Choose angle',   icon: selectedAngle         ? <CheckCircle size={16} /> : <Clock size={16} />       },
              { title: 'AI Model', description: resolvedModel         ? resolvedModel.label : 'Choose model',   icon: selectedModelValue     ? <CheckCircle size={16} /> : <Bot size={16} />         },
              { title: 'YouTube',  description: youtubeUrl.trim()     ? 'URL ready'         : 'Paste URL',      icon: youtubeUrl.trim()     ? <CheckCircle size={16} /> : <Youtube size={16} />     },
              { title: 'Prompt',   description: selectedLibraryPrompt ? selectedLibraryPrompt.name : 'Select prompt', icon: selectedLibraryPrompt ? <CheckCircle size={16} /> : <BookOpen size={16} /> },
              { title: 'Done',     description: isSetupReady          ? 'Ready to generate' : 'Complete steps', icon: isSetupReady          ? <CheckCircle size={16} /> : <AlertCircle size={16} /> },
            ] : [
              { title: 'Angle',    description: selectedAngle         ? 'Selected'           : 'Choose angle',   icon: selectedAngle         ? <CheckCircle size={16} /> : <Clock size={16} />       },
              { title: 'AI Model', description: resolvedModel         ? resolvedModel.label  : 'Choose model',   icon: selectedModelValue     ? <CheckCircle size={16} /> : <Bot size={16} />         },
              { title: 'Keyword',  description: selectedKeyword       ? 'Selected'           : 'Choose keyword', icon: selectedKeyword       ? <CheckCircle size={16} /> : <Tags size={16} />        },
              { title: 'CTA',      description: selectedCta           ? 'Selected'           : 'Choose CTA',     icon: selectedCta           ? <CheckCircle size={16} /> : <Target size={16} />      },
              { title: 'Prompt',   description: selectedLibraryPrompt ? selectedLibraryPrompt.name : 'Select prompt', icon: selectedLibraryPrompt ? <CheckCircle size={16} /> : <BookOpen size={16} /> },
              { title: 'Done',     description: isSetupReady          ? 'Ready to generate'  : 'Complete steps', icon: isSetupReady          ? <CheckCircle size={16} /> : <AlertCircle size={16} /> },
            ]} />
          </div>

          {/* Selected question indicator */}
          {selectedQuestionData ? (
            <div style={{ marginBottom: '16px', padding: '8px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '13px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={14} />
              <span>Selected: <strong>{selectedQuestionData.questionTitle || selectedQuestionData.questionLink}</strong></span>
            </div>
          ) : (
            <div style={{ marginBottom: '16px', padding: '8px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '13px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={14} />
              <span>No question selected — check a row in the table below to use <strong>Generate Selected</strong> or <strong>Publish Selected</strong>.</span>
            </div>
          )}

          {/* Angle + Model */}
          <Row gutter={24} style={{ marginBottom: '16px' }}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '8px' }}><Text strong>Select Angle</Text></div>
              <Select placeholder="Choose an angle" style={{ width: '100%' }} value={selectedAngle} onChange={handleAngleSelect} loading={loadingAngles} size="large">
                {angles.map(a => <Option key={a._id} value={a._id}>{a.angleName}</Option>)}
              </Select>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: '8px' }}><Text strong><Bot size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Select AI Model</Text></div>
              <Select showSearch placeholder="Choose AI model..." style={{ width: '100%' }} value={selectedModelValue} onChange={setSelectedModelValue} size="large" optionFilterProp="label" filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}>
                {Object.entries(AI_MODELS).map(([key, group]) => (
                  <OptGroup key={key} label={<span style={{ fontWeight: 600, fontSize: '13px' }}>{group.icon}&nbsp;{group.label}</span>}>
                    {group.models.map(m => <Option key={m.value} value={m.value} label={m.label}>{m.label}</Option>)}
                  </OptGroup>
                ))}
              </Select>
            </Col>
          </Row>

          {/* API key warning */}
          {resolvedModel && (() => {
            const creds = JSON.parse(localStorage.getItem('user') || '{}');
            return !{ openai: creds.chatgptApiKey, gemini: creds.geminiApiKey, claude: creds.claudeApiKey }[resolvedModel.provider];
          })() && (
            <div style={{ marginBottom: '16px', padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '13px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bot size={15} />
              <span>Using <strong>{resolvedModel.label}</strong> — Save your <strong>{resolvedModel.providerLabel}</strong> API key in <strong>Settings → API Keys</strong>.</span>
            </div>
          )}

          {/* Keyword + CTA — Website mode only */}
          {selectedAngleDetails && !isYoutubeMode && (
            <div>
              <Divider />
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '8px' }}><Text strong><Tags size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Select Keyword</Text></div>
                  <Select placeholder="Choose keyword to focus on" style={{ width: '100%' }} value={selectedKeyword} onChange={setSelectedKeyword} disabled={!selectedAngleDetails.keywords?.length}>
                    {selectedAngleDetails.keywords?.map((kw, i) => <Option key={i} value={kw}>{kw}</Option>)}
                  </Select>
                  {!selectedAngleDetails.keywords?.length && <Text type="secondary" style={{ fontSize: '12px' }}>No keywords for this angle</Text>}
                </Col>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '8px' }}><Text strong><Target size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Select CTA</Text></div>
                  <Select placeholder="Choose CTA to include" style={{ width: '100%' }} value={selectedCta} onChange={setSelectedCta} disabled={!selectedAngleDetails.ctas?.length}>
                    {selectedAngleDetails.ctas?.map((cta, i) => <Option key={i} value={cta}>{cta}</Option>)}
                  </Select>
                  {!selectedAngleDetails.ctas?.length && <Text type="secondary" style={{ fontSize: '12px' }}>No CTAs for this angle</Text>}
                </Col>
              </Row>
              {selectedAngleDetails.basicInfo && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Basic Info:</Text>
                  <div style={{ marginTop: '4px', padding: '8px', background: '#fafafa', borderRadius: '4px', fontSize: '12px' }}>{selectedAngleDetails.basicInfo}</div>
                </div>
              )}
              {/* NEW: Brand Voice preview — only shown if angle has brandVoice set */}
              {selectedAngleDetails.brandVoice && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Brand Voice:</Text>
                  <div style={{ marginTop: '4px', padding: '8px', background: '#fff0f6', border: '1px solid #ffadd2', borderRadius: '4px', fontSize: '12px', color: '#531dab' }}>
                    {selectedAngleDetails.brandVoice.length > 200
                      ? selectedAngleDetails.brandVoice.substring(0, 200) + '…'
                      : selectedAngleDetails.brandVoice}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* YouTube mode: show basic info + brand voice */}
          {selectedAngleDetails && isYoutubeMode && (
            <div style={{ marginTop: '8px', marginBottom: '16px' }}>
              <Divider />
              {selectedAngleDetails.basicInfo && (
                <>
                  <Text strong>Basic Info:</Text>
                  <div style={{ marginTop: '4px', marginBottom: '12px', padding: '8px', background: '#fafafa', borderRadius: '4px', fontSize: '12px' }}>{selectedAngleDetails.basicInfo}</div>
                </>
              )}
              {/* NEW: Brand Voice preview in YouTube mode */}
              {selectedAngleDetails.brandVoice && (
                <>
                  <Text strong>Brand Voice:</Text>
                  <div style={{ marginTop: '4px', padding: '8px', background: '#fff0f6', border: '1px solid #ffadd2', borderRadius: '4px', fontSize: '12px', color: '#531dab' }}>
                    {selectedAngleDetails.brandVoice.length > 200
                      ? selectedAngleDetails.brandVoice.substring(0, 200) + '…'
                      : selectedAngleDetails.brandVoice}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <Divider style={{ marginBottom: '16px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>

            {/* LEFT: Prompt buttons */}
            <Space size={8}>
              <Tooltip title="Select a prompt from your Prompt Library">
                <Button
                  icon={<BookOpen size={15} />}
                  onClick={() => setPromptLibraryModal(true)}
                  style={{ borderColor: selectedLibraryPrompt ? '#7c3aed' : '#d9d9d9', color: selectedLibraryPrompt ? '#7c3aed' : undefined, fontWeight: selectedLibraryPrompt ? '600' : '400', background: selectedLibraryPrompt ? '#f5f3ff' : undefined }}
                >
                  {selectedLibraryPrompt ? `📝 ${selectedLibraryPrompt.name}` : '📚 Select Prompt'}
                </Button>
              </Tooltip>

              {selectedLibraryPrompt && (
                <>
                  <Tooltip title="Manually edit the selected prompt">
                    <Button icon={<FileText size={15} />} onClick={handlePromptModalOpen}>
                      Edit Prompt
                    </Button>
                  </Tooltip>
                  <Tooltip title="Clear selected prompt">
                    <Button size="small" danger onClick={handleClearLibraryPrompt}>✕ Clear</Button>
                  </Tooltip>
                </>
              )}
            </Space>

            {/* RIGHT: Generate / Publish buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Tooltip title={!selectedQuestionData ? 'Select a row from the table first' : !isSetupReady ? 'Complete all setup steps first' : 'Generate AI answer for the selected question'}>
                <Button icon={<Zap size={14} />} size="middle" loading={generatingSelected} disabled={anyBusy && !generatingSelected} onClick={handleGenerateSelected} style={{ fontWeight: '500', borderColor: isReadyToGenerate ? '#7c3aed' : '#d9d9d9', color: isReadyToGenerate ? '#7c3aed' : '#bfbfbf' }}>
                  {generatingSelected ? 'Generating…' : 'Generate Selected'}
                </Button>
              </Tooltip>

              <Tooltip title={!selectedQuestionData ? 'Select a row from the table first' : 'Publish the selected question answer to Quora'}>
                <Button icon={<Send size={14} />} size="middle" loading={publishingSelected} disabled={anyBusy && !publishingSelected} onClick={handlePublishSelected} style={{ fontWeight: '500', borderColor: selectedQuestionData ? '#0ea5e9' : '#d9d9d9', color: selectedQuestionData ? '#0ea5e9' : '#bfbfbf' }}>
                  {publishingSelected ? 'Publishing…' : 'Publish Selected'}
                </Button>
              </Tooltip>

              <div style={{ width: '1px', height: '22px', backgroundColor: '#e5e7eb' }} />

              <Tooltip title={!isSetupReady ? 'Complete all setup steps first' : "Generate AI answers for all questions that don't have one yet"}>
                <Button icon={<Zap size={14} />} size="middle" loading={bulkGenerating} disabled={anyBusy && !bulkGenerating} onClick={handleGenerateAllAnswers} style={{ fontWeight: '500', borderColor: isSetupReady ? '#7c3aed' : '#d9d9d9', color: isSetupReady ? '#fff' : '#bfbfbf', backgroundColor: isSetupReady ? '#7c3aed' : '#fafafa' }}>
                  {bulkGenerating ? 'Generating All…' : 'Generate All'}
                </Button>
              </Tooltip>

              <Tooltip title="Publish all questions that already have a generated answer">
                <Button icon={<Send size={14} />} size="middle" loading={bulkPublishing} disabled={anyBusy && !bulkPublishing} onClick={handlePublishAllAnswers} style={{ fontWeight: '500', borderColor: '#16a34a', color: '#fff', backgroundColor: '#16a34a' }}>
                  {bulkPublishing ? 'Publishing All…' : 'Publish All'}
                </Button>
              </Tooltip>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Edit Prompt Modal ─────────────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={18} />
            <span>Edit Prompt</span>
            {isYoutubeMode ? <Tag color="red" style={{ marginLeft: '4px', fontSize: '11px' }}>YouTube Mode</Tag> : <Tag color="blue" style={{ marginLeft: '4px', fontSize: '11px' }}>Website Mode</Tag>}
            {selectedLibraryPrompt && <Tag color="purple" style={{ marginLeft: '4px', fontSize: '11px' }}>From Library: {selectedLibraryPrompt.name}</Tag>}
          </div>
        }
        visible={promptModalVisible}
        onCancel={() => setPromptModalVisible(false)}
        width={800}
        footer={[
          <Button key="reset" onClick={() => { setSelectedLibraryPrompt(null); setEditablePrompt(''); setPromptModalVisible(false); toast.info('Prompt cleared'); }}>Clear Prompt</Button>,
          <Button key="close" onClick={() => setPromptModalVisible(false)}>Cancel</Button>,
          <Button key="save" type="primary" onClick={() => setPromptModalVisible(false)}>Save Changes</Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text strong>Your Prompt</Text>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '10px', marginTop: '4px' }}>
            {isYoutubeMode ? 'YouTube mode is active. Use {transcript} to inject the video transcript, and {youtube_url} to embed your video link inside the answer.' : 'Website mode is active. {transcript} is not available here.'}
            {' '}Click a chip below to insert a variable at your cursor.
          </Text>
          <VariableChips textareaRef={promptTextareaRef} value={editablePrompt} onChange={setEditablePrompt} isYoutubeMode={isYoutubeMode} />
          <TextArea ref={promptTextareaRef} value={editablePrompt} onChange={(e) => setEditablePrompt(e.target.value)} rows={18} style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '13px' }}
            placeholder={isYoutubeMode ? 'Example:\nAnswer this Quora question: {question}\n\nVideo transcript:\n{transcript}\n\nExisting answers:\n{existing_answers}' : 'Example:\nAnswer this Quora question: {question}\n\nUse keyword: {keyword}\nInclude CTA: {cta}\nContext: {basic_info}\n\nExisting answers:\n{existing_answers}'}
          />
        </div>
        {selectedLibraryPrompt ? (
          <div style={{ color: '#7c3aed', fontSize: '12px' }}>📚 Using prompt from library: <strong>{selectedLibraryPrompt.name}</strong></div>
        ) : editablePrompt.trim() ? (
          <div style={{ color: '#1890ff', fontSize: '12px' }}>✓ Using your edited prompt (not saved — resets on page reload)</div>
        ) : (
          <div style={{ color: '#fa8c16', fontSize: '12px' }}>⚠ Using default prompt — select from Prompt Library for consistent results</div>
        )}
      </Modal>

      {/* ── Prompt Library Select Modal ───────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpen size={18} style={{ color: '#7c3aed' }} />
            <span>Select a Prompt</span>
            {isYoutubeMode ? <Tag color="red" style={{ fontSize: '11px' }}>YouTube Mode</Tag> : <Tag color="blue" style={{ fontSize: '11px' }}>Website Mode</Tag>}
          </div>
        }
        visible={promptLibraryModal}
        onCancel={() => setPromptLibraryModal(false)}
        width={720}
        footer={[<Button key="close" onClick={() => setPromptLibraryModal(false)}>Cancel</Button>]}
      >
        {promptLibrary.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <BookOpen size={44} style={{ color: '#d9d9d9', display: 'block', margin: '0 auto 12px' }} />
            <div style={{ color: '#888', marginBottom: '6px', fontSize: '14px' }}>No prompts in your library yet.</div>
            <div style={{ fontSize: '12px', color: '#aaa' }}>Go to <strong>Prompt Library</strong> in the sidebar to add prompts.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
              Showing prompts for <strong>{isYoutubeMode ? 'YouTube' : 'Website'}</strong> mode (mode: "both" always shown).
            </div>
            {promptLibrary
              .filter(p => p.mode === 'both' || (isYoutubeMode ? p.mode === 'youtube' : p.mode === 'website'))
              .map(prompt => (
                <div
                  key={prompt._id}
                  onClick={() => handleSelectFromLibrary(prompt)}
                  style={{ padding: '12px 16px', border: selectedLibraryPrompt?._id === prompt._id ? '2px solid #7c3aed' : '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: selectedLibraryPrompt?._id === prompt._id ? '#faf5ff' : '#fff', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => { if (selectedLibraryPrompt?._id !== prompt._id) e.currentTarget.style.borderColor = '#c4b5fd'; }}
                  onMouseLeave={e => { if (selectedLibraryPrompt?._id !== prompt._id) e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Text strong style={{ fontSize: '13px' }}>{prompt.name}</Text>
                        {prompt.mode === 'youtube' && <Tag color="red"    style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px' }}>YouTube</Tag>}
                        {prompt.mode === 'website' && <Tag color="blue"   style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px' }}>Website</Tag>}
                        {prompt.mode === 'both'    && <Tag color="purple" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 5px' }}>Both</Tag>}
                      </div>
                      {prompt.description && <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>{prompt.description}</div>}
                      <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace', background: '#f9f9f9', padding: '6px 8px', borderRadius: '4px', maxHeight: '54px', overflow: 'hidden' }}>
                        {prompt.content.substring(0, 130)}{prompt.content.length > 130 ? '…' : ''}
                      </div>
                    </div>
                    {selectedLibraryPrompt?._id === prompt._id && <CheckCircle size={18} style={{ color: '#7c3aed', marginLeft: '12px', flexShrink: 0 }} />}
                  </div>
                </div>
              ))
            }
            {promptLibrary.filter(p => p.mode === 'both' || (isYoutubeMode ? p.mode === 'youtube' : p.mode === 'website')).length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px' }}>
                No prompts match the current mode. Add prompts tagged "{isYoutubeMode ? 'YouTube' : 'Website'}" or "Both" in Prompt Library.
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default AiAnswerGenerator;